import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ProviderConsumer, Text } from '@fluentui/react-northstar';
import { PersonType, UserType } from '@microsoft/mgt-components';
import { PeoplePicker } from '@microsoft/mgt-react';
import i18next from 'i18next';
import { platformService } from '../../../shared/services/platform.service';
import { searchUsers } from '../../../shared/cache/PlatformUserCache';
import { ThemeColorScheme } from '../../../shared/common/TeamsTheme';
import { getDropdownBoxShadow } from '../../ticketHelper';
import { ADMINISTRATORS, ANALYSTS, MyWorkListViewType, TicketApprovalState } from '../../../shared/utils/constants';
import { isLifecycleRunning } from '../../../shared/utils/lifecyclePhaseFilter';
import { CELL_BOX, InlineEditRefused, PANEL_PORTAL, byCodeUnit, markClass, reportFailure, savingCell, useSaveMark, focusCellBack } from '../../../shared/components/table/inlineCell';
import { usePanelPlacement } from '../../../shared/components/table/TypeaheadList';
import { FieldErrorText, RequiredAsterisk } from '../../../shared/components/FieldErrorText';
import { reloadAfterSave } from '../../../shared/components/table/InlineFieldCells';
import { PanelActions, focusActions, holdOutsideClick, isPanelAction, pickerListOpen, wrapActionTab } from '../../../shared/components/table/PanelActions';
import { ListUser, PickedPerson, personKey, pickedFrom, toPerson, toPlatformUser } from '../../../shared/components/table/InlineRequesterCell';
import { MyWorkEditContext, refuseUnlessPhaseActive } from './MyWorkInlineCells';

/** An approval row as GetMyWork sends it: Id is the ticket's link to the approval (TicketApprovals), not the approval. */
export interface MyWorkApprovalRow {
  Id?: number;
  ItemType?: number;
  TicketId: number;
  StatusId?: number | null;
  LifecyclePhaseName?: string;
  LifecycleStatusId?: string;
  /** The approval's ticket is closed or merged (GetMyWork). */
  TicketClosed?: boolean;
  TicketMerged?: boolean;
  /** The approvers, as users - useMyWorkListFunctions puts them where a ticket's collaborators go. */
  TicketCollaborators?: (ListUser | undefined)[];
}

interface ApprovalRecord {
  Id: number;
  Title?: string;
  AdditionalDetails?: string;
  IsRequiredByAll?: boolean;
  ApprovalState?: number;
}

const PANEL_WIDTH = 340;

/**
 * Who may change an approval's approvers from My Work - the ticket page's rule (TicketApprovalsList,
 * TicketLifecyclePhaseContainer): an Administrator or Analyst, on a pending approval, and for a lifecycle
 * approval only while the lifecycle runs, on a ticket that is neither closed nor merged, by a licensed analyst. All
 * but the role are asked again at save, for a row gone stale.
 */
const canChangeApprovers = (row: MyWorkApprovalRow, roles: string[], licensed: boolean) =>
  licensed && row.ItemType === MyWorkListViewType.Approvals && !row.TicketClosed && !row.TicketMerged &&
  (roles.includes(ADMINISTRATORS) || roles.includes(ANALYSTS)) &&
  row.StatusId === TicketApprovalState.Pending &&
  (!row.LifecyclePhaseName || isLifecycleRunning(row.LifecycleStatusId));

/** The approval behind a row: the row's Id is the TicketApprovals link, and the change is made on the approval. */
const findApproval = async (row: MyWorkApprovalRow): Promise<ApprovalRecord | undefined> => {
  const links: { Id: number; Approval?: ApprovalRecord }[] = await new platformService().getTicketApprovals(row.TicketId);
  return links.find(link => link.Id === row.Id)?.Approval;
};

/**
 * An approval's approvers on My Work, changed in a panel with the people picker the ticket page uses, saved
 * through the ticket page's call (UpdateTicketApprovals) with the approval's own title, details and
 * required-by-all sent back unchanged. Keys as the Requester panel: Escape closes the suggestions, then the
 * panel; Tab or Enter with a person lit takes them; Enter with none lit saves. Saved only by Save (or that
 * Enter), which lights when the approvers changed and at least one is left.
 */
export const MyWorkApproversCell = ({ row, resting }: { row: MyWorkApprovalRow; resting: React.ReactElement }) => {
  const context = useContext(MyWorkEditContext);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [empty, setEmpty] = useState(false);
  const cellRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Follows the cell as the grid scrolls either way, and stays within the view's left and right.
  const position = usePanelPlacement(cellRef, panelRef, open, PANEL_WIDTH, 200);
  const pickedRef = useRef<PickedPerson[] | null>(null);
  const closingRef = useRef(false);
  /** Set to reopen the panel after a failed save with the agent's picks in it, not the approval's approvers. */
  const reopeningRef = useRef(false);
  const approversKey = (row.TicketCollaborators ?? []).filter((user): user is ListUser => !!user).map(user => user.Id).join(',');
  const saveMark = useSaveMark(row.Id, approversKey, saving, 'approvers');
  /** Redraws when the picks change, so Save can light - they live in a ref (see InlineRequesterCell). */
  const [, setPicksChanged] = useState(0);
  const dirtyRef = useRef(false);
  const canSaveRef = useRef(false);

  const approvers = (row.TicketCollaborators ?? []).filter((user): user is ListUser => !!user);

  // A refetch can hand this cell another row: stop looking busy. New approvers for this row - put in by its own
  // save as it starts, or by a reload - only close it.
  useEffect(() => { setSaving(false); }, [row.Id]);
  useEffect(() => { setOpen(false); }, [row.Id, approversKey]);


  // Seeded once the panel exists, and focused after the picker has redrawn - see InlineRequesterCell.
  const panelShown = open && position != null;
  useEffect(() => {
    if (!panelShown) return;
    const picker = panelRef.current?.querySelector<HTMLElement & { selectedPeople: PickedPerson[]; updateComplete?: Promise<unknown> }>('mgt-people-picker');
    if (!picker) return;
    const seed = reopeningRef.current && pickedRef.current ? pickedRef.current : approvers.map(user => toPerson(user));
    reopeningRef.current = false;
    picker.selectedPeople = seed;
    setEmpty(!seed.length);
    (picker.updateComplete ?? Promise.resolve()).then(() => picker.focus());
  }, [panelShown]);

  // A click outside the cell and the panel closes it - unless the approvers changed, which only Save, Cancel or
  // Escape settle. composedPath reaches into the picker's shadow root, so its own suggestion list counts as inside.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      const path = event.composedPath();
      if (path.includes(panelRef.current) || path.includes(cellRef.current)) return;
      if (dirtyRef.current) holdOutsideClick(event, panelRef.current, cellRef.current);
      else close(false, false);
    };
    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [open]);

  const roles: string[] = context?.currentState.userRoles?.roles ?? [];
  if (!canChangeApprovers(row, roles, !!context?.ticketsEditable)) return resting;

  const approversDiffer = (picked: PickedPerson[] | null) =>
    picked != null && picked.map(personKey).sort(byCodeUnit).join('|') !== approvers.map(user => personKey(toPerson(user))).sort(byCodeUnit).join('|');
  const dirty = approversDiffer(pickedRef.current);
  const canSave = dirty && pickedRef.current.length > 0;
  dirtyRef.current = dirty;
  canSaveRef.current = canSave;

  const save = async () => {
    const picked = pickedRef.current;
    if (!picked) return;
    const changed = approversDiffer(picked);
    // An approval needs at least one approver: left empty, it keeps the ones it has.
    if (!changed || !picked.length) return;

    saveMark.begin();
    setSaving(true);
    let patched = false;
    try {
      const api = new platformService();
      const { data } = await api.getTicket(row.TicketId, '?$select=Closed,IsMerged');
      if (data?.Closed || data?.IsMerged) throw new InlineEditRefused(i18next.t('ticket.ticket-board.closed-ticket-cannot-update'));
      await refuseUnlessPhaseActive(row);
      const approval = await findApproval(row);
      if (!approval) throw new Error(`No approval behind My Work row ${row.Id}`);
      // Asked again just before saving, as the ticket page does: the server would change a decided approval.
      const [current] = await api.getApproval(approval.Id);
      if (current?.ApprovalState !== TicketApprovalState.Pending) {
        throw new InlineEditRefused(i18next.t(current?.ApprovalState === TicketApprovalState.Approved
          ? 'ticket-details.edit-ticket-form.approval.error.update-already-approved'
          : 'ticket-details.edit-ticket-form.approval.error.update-already-rejected'));
      }
      const users = await Promise.all(picked.map(person => searchUsers(toPlatformUser(person))));
      const ids = users.map(user => user.Id);
      // The row shows them while the save is out (an approval row keeps its approvers in TicketCollaborators).
      context.patch(row, { TicketCollaborators: users });
      patched = true;
      await api.updateTicketApprovalApprovers(approval.Id, ids, JSON.stringify({
        Title: current.Title ?? approval.Title,
        AdditionalDetails: current.AdditionalDetails ?? approval.AdditionalDetails ?? '',
        IsRequiredByAll: current.IsRequiredByAll ?? approval.IsRequiredByAll ?? false
      }));
      reloadAfterSave(context.refresh);
      setSaving(false);
      saveMark.show('saved');
    } catch (error: unknown) {
      if (patched) context.patch(row, { TicketCollaborators: approvers });
      const outcome = reportFailure(error, {});
      setSaving(false);
      saveMark.show(outcome);
      await context.refresh().catch(() => undefined);
      if (outcome === 'failed') {
        reopeningRef.current = true;
        closingRef.current = false;
        setOpen(true);
      }
    }
  };

  const openPanel = () => {
    saveMark.opening();
    closingRef.current = false;
    pickedRef.current = null;
    setOpen(true);
  };

  const close = (keep: boolean, refocus: boolean) => {
    if (closingRef.current) return;
    closingRef.current = true;
    setOpen(false);
    if (keep) save();
    if (refocus) focusCellBack(cellRef.current);
  };

  type Picker = HTMLElement & { hideFlyout?: () => void };
  const suggestionsShowing = () => {
    const picker = panelRef.current?.querySelector<Picker>('mgt-people-picker');
    return pickerListOpen(picker) ? picker : null;
  };

  const onPanelKeyDownCapture = (event: React.KeyboardEvent) => {
    const showing = suggestionsShowing();
    if (event.key === 'Escape' && showing) {
      event.preventDefault();
      event.stopPropagation();
      showing.hideFlyout?.();
      return;
    }
    // As in Add Ticket: with a list showing, Tab and Enter take the lit person and stay (after a pick the list
    // shows again, first person lit - Escape it to move on). With none, Tab goes to the buttons and Enter saves
    // (or closes, when nothing changed). See pickerListOpen for what "showing" means.
    if (event.key === 'Tab' && showing) { event.preventDefault(); return; }
    if (showing || event.target === panelRef.current || isPanelAction(event.target)) return;
    if (event.key === 'Tab' || event.key === 'Enter') event.stopPropagation();
    if (event.key === 'Tab') {
      event.preventDefault();
      focusActions(panelRef.current, event.shiftKey);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (canSaveRef.current) close(true, true);
      else if (!dirtyRef.current) close(false, true);
    }
  };

  const onPanelKeyDown = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'Escape') { event.preventDefault(); close(false, true); return; }
    const picker = panelRef.current?.querySelector<HTMLElement>('mgt-people-picker');
    if (picker) wrapActionTab(event, panelRef.current, [picker]);
  };

  const stop = (event: React.SyntheticEvent) => event.stopPropagation();

  return (
    <ProviderConsumer
      render={globalTheme => {
        const marked = markClass(saveMark.mark, saveMark.edited);
        const lock = savingCell(saveMark.busy);
        return (
          <>
            <div
              ref={cellRef}
              className={`inline-edit-cell${marked}${lock.className}`}
              {...lock.props}
              data-selection-disabled={true}
              tabIndex={0}
              role="button"
              aria-label={`${i18next.t('ticket-details.edit-ticket-form.approval.dialog.form.approvers')}: ${approvers.map(user => user.FullName).join(', ')}`}
              aria-expanded={open}
              onMouseDown={stop}
              onClick={event => { stop(event); if (!open) openPanel(); }}
              onKeyDown={event => {
                if (event.key === 'Escape' && open) { stop(event); event.preventDefault(); close(false, true); return; }
                if (!open && (event.key === 'Enter' || event.key === 'F2')) { stop(event); event.preventDefault(); openPanel(); }
              }}
              style={{
                ...CELL_BOX,
                
                cursor: 'text',
                outline: open ? `1px solid ${globalTheme.siteVariables.colorScheme.brand.foreground}` : undefined,
                outlineOffset: -1, ...saveMark.markStyle
              }}
            >
              {resting}
            </div>
            {open && position && ReactDOM.createPortal(
              <div
                ref={panelRef}
                {...PANEL_PORTAL}
                role="dialog"
                tabIndex={-1}
                aria-label={i18next.t('ticket-details.edit-ticket-form.approval.dialog.form.approvers')}
                className="rounded"
                onMouseDown={stop}
                onClick={stop}
                onDoubleClick={stop}
                onKeyDownCapture={onPanelKeyDownCapture}
                onKeyDown={onPanelKeyDown}
                style={{
                  position: 'fixed',
                  left: position.left,
                  ...(position.bottom === undefined ? { top: position.top } : { bottom: position.bottom }),
                  width: PANEL_WIDTH,
                  zIndex: 1000000,
                  outline: 'none',
                  padding: 12,
                  backgroundColor: globalTheme.siteVariables.colorScheme.default.background,
                  // The panel is portalled to the page's body, outside the themed app, so its text takes the theme's colour here.
                  color: globalTheme.siteVariables.colorScheme.default.foreground,
                  boxShadow: getDropdownBoxShadow(globalTheme),
                  ...ThemeColorScheme(globalTheme.siteVariables)
                }}
              >
                <Text style={{ fontSize: 14 }}>{i18next.t('ticket-details.edit-ticket-form.approval.dialog.form.approvers')}<RequiredAsterisk /></Text>
                <div className="rounded" aria-invalid={empty}>
                  <PeoplePicker
                    userType={UserType.user}
                    type={PersonType.person}
                    selectionChanged={(event: Event) => {
                      const picked = pickedFrom(event);
                      pickedRef.current = [...picked];
                      setEmpty(picked.length === 0);
                      setPicksChanged(n => n + 1);
                    }}
                  />
                </div>
                {empty && <FieldErrorText>{i18next.t('ticket-details.edit-ticket-view.required-field-must-be-filled-out')}</FieldErrorText>}
                <div style={{ marginTop: 12 }}>
                  <PanelActions canSave={canSave} onSave={() => close(true, true)} onCancel={() => close(false, true)} />
                </div>
              </div>,
              document.body
            )}
          </>
        );
      }}
    />
  );
};
